import React from 'react';
import { resolveCurrencyCode } from '../../../utils/currencyUtils';
import { OtherAmountRow, DropdownOption } from './inTransactionTypes';

interface InOtherAmountsSectionProps {
  otherAmounts: OtherAmountRow[];
  currencyOptions: DropdownOption[];
  accountOptions: DropdownOption[];
  currencies: any[] | null | undefined;
  onAdd: () => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, field: keyof OtherAmountRow, value: any) => void;
}

const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {children}
  </div>
);

const InOtherAmountsSection: React.FC<InOtherAmountsSectionProps> = ({
  otherAmounts,
  currencyOptions,
  accountOptions,
  currencies,
  onAdd,
  onRemove,
  onUpdate,
}) => {
  return (
    <div className="bg-gray-50 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center">
          <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Other Amounts
        </h3>
        <button
          type="button"
          onClick={onAdd}
          className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          + Add Amount
        </button>
      </div>

      {otherAmounts.length === 0 && (
        <p className="text-sm text-gray-500 italic">No additional amounts. Click "Add Amount" to add more.</p>
      )}

      <div className="space-y-4">
        {otherAmounts.map((row, index) => (
          <div key={row.id} className="bg-white p-4 rounded-lg border border-gray-200 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label={`Amount ${index + 2}`}>
                <input
                  type="number" step="0.01" min="0" value={row.amount}
                  onChange={(e) => onUpdate(row.id, 'amount', parseFloat(e.target.value) || 0)}
                  className={inputCls} placeholder="0.00"
                />
              </Field>
              <Field label="Collected Currency">
                <select
                  value={row.currency}
                  onChange={(e) => onUpdate(row.id, 'currency', e.target.value)}
                  className={inputCls}
                >
                  <option value="">Select Currency</option>
                  {currencyOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </Field>
              {row.currency && resolveCurrencyCode(row.currency, currencies as any[]) !== 'EGP' && (
                <Field label="Exchange Rate">
                  <input
                    type="number" step="0.01" min="0" value={row.exchangeRate || ''}
                    onChange={(e) => onUpdate(row.id, 'exchangeRate', parseFloat(e.target.value) || undefined)}
                    className={inputCls} placeholder="Rate"
                  />
                </Field>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <Field label="Cheque Date">
                <input
                  type="date" value={row.chequeDate || ''}
                  onChange={(e) => onUpdate(row.id, 'chequeDate', e.target.value)}
                  className={inputCls}
                />
              </Field>
              <Field label="Cheque Number">
                <input
                  type="text" value={row.chequeNumber || ''}
                  onChange={(e) => onUpdate(row.id, 'chequeNumber', e.target.value)}
                  className={inputCls} placeholder="Cheque number"
                />
              </Field>
              <Field label="Bank Name">
                <input
                  type="text" value={row.bankName || ''}
                  onChange={(e) => onUpdate(row.id, 'bankName', e.target.value)}
                  className={inputCls} placeholder="Issuing bank"
                />
              </Field>
              <Field label="Money In Account">
                <select
                  value={row.depositAccount || ''}
                  onChange={(e) => onUpdate(row.id, 'depositAccount', e.target.value)}
                  className={inputCls}
                >
                  <option value="">Select account</option>
                  {accountOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => onRemove(row.id)}
                className="px-3 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 focus:outline-none"
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default InOtherAmountsSection;
